import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="container my-5 p-4 bg-white rounded">
      <h5
        className="mb-3 fw-bold"
        style={{
          background: "linear-gradient(90deg, #A14BFF, #0F81FF)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Okey Doke
      </h5>
      <h1 className="mb-4 fw-bold">Privacy Policy</h1>

      <p>
        We respect your privacy and are committed to protecting it through our
        compliance with this privacy policy (“Policy”). This Policy describes
        the types of information we may collect from you or that you may provide
        (“Personal Information”) in the “Okey Doke” mobile application (“Mobile
        Application” or “Service”) and any of its related products and services
        (collectively, “Services”), and our practices for collecting, using,
        maintaining, protecting, and disclosing that Personal Information. It
        also describes the choices available to you regarding our use of your
        Personal Information and how you can access and update it.
      </p>

      <p>
        This Policy is a legally binding agreement between you (“User”, “you” or
        “your”) and Sirilex LLC (“Sirilex LLC”, “we”, “us” or “our”). If you are
        entering into this Policy on behalf of a business or other legal entity,
        you represent that you have the authority to bind such entity to this
        Policy, in which case the terms “User”, “you” or “your” shall refer to
        such entity. If you do not have such authority, or if you do not agree
        with the terms of this Policy, you must not accept this Policy and may
        not access and use the Mobile Application and Services. By accessing and
        using the Mobile Application and Services, you acknowledge that you have
        read, understood, and agree to be bound by the terms of this Policy.
        This Policy does not apply to the practices of companies that we do not
        own or control, or to individuals that we do not employ or manage.
      </p>

      <h2 className="h5 mt-4">Table of Contents</h2>
      <ul>
        <li>Collection of personal information</li>
        <li>Use and processing of collected information</li>
        <li>Managing information</li>
        <li>Disclosure of information</li>
        <li>Retention of information</li>
        <li>Transfer of information</li>
        <li>Region specific notices</li>
        <li>How to exercise your rights</li>
        <li>Privacy of children</li>
        <li>Links to other resources</li>
        <li>Information security</li>
        <li>Data breach</li>
        <li>Changes and amendments</li>
        <li>Acceptance of this policy</li>
        <li>Contacting us</li>
      </ul>

      <h2 className="h5 mt-4">Collection of Personal Information</h2>
      <p>
        You can access and use the Mobile Application and Services without
        telling us who you are or revealing any information by which someone
        could identify you as a specific, identifiable individual. If, however,
        you wish to use some of the features offered in the Mobile Application,
        you may be asked to provide certain Personal Information (for example,
        your name and email address).
      </p>

      <p>
        We receive and store any information you knowingly provide to us when
        you create an account, or fill any forms in the Mobile Application. When
        required, this information may include the following:
      </p>

      <ul>
        <li>
          Account details (such as user name, unique user ID, password, etc)
        </li>
        <li>Contact information (such as email address, phone number, etc)</li>
        <li>
          Basic personal information (such as name, country of residence, etc)
        </li>
        <li>
          Payment information (such as credit card details, bank details, etc)
        </li>
      </ul>

      <p>
        Some of the information we collect is directly from you via the Mobile
        Application and Services. However, we may also collect Personal
        Information about you from other sources such as social media platforms
        and our joint partners. Personal Information we collect from other
        sources may include demographic information, such as age and gender,
        device information, such as IP addresses, location, such as city and
        state, and online behavioral data, such as information about your use of
        social media websites, page view information and search results and
        links.
      </p>

      <p>
        You can choose not to provide us with your Personal Information, but
        then you may not be able to take advantage of some of the features in
        the Mobile Application. Users who are uncertain about what information
        is mandatory are welcome to contact us.
      </p>

      <h2 className="h5 mt-4">Use and Processing of Collected Information</h2>
      <p>
        We act as a data controller and a data processor when handling Personal
        Information, unless we have entered into a data processing agreement
        with you in which case you would be the data controller and we would be
        the data processor.
      </p>

      <p>
        Our role may also differ depending on the specific situation involving
        Personal Information. We act in the capacity of a data controller when
        we ask you to submit your Personal Information that is necessary to
        ensure your access and use of the Mobile Application and Services. In
        such instances, we are a data controller because we determine the
        purposes and means of the processing of Personal Information.
      </p>

      <p>
        In order to make the Mobile Application and Services available to you,
        or to meet a legal obligation, we may need to collect and use certain
        Personal Information. If you do not provide the information that we
        request, we may not be able to provide you with the requested products
        or services. Any of the information we collect from you may be used for
        the following purposes:
      </p>

      <ul>
        <li>Create and manage user accounts</li>
        <li>Fulfill and manage orders</li>
        <li>Improve user experience</li>
        <li>Run and operate the Mobile Application and Services</li>
      </ul>

      <p>
        Processing your Personal Information depends on how you interact with
        the Mobile Application and Services, where you are located in the world
        and if one of the following applies: (a) you have given your consent for
        one or more specific purposes; (b) provision of information is necessary
        for the performance of this Policy with you and/or for any
        pre-contractual obligations thereof; (c) processing is necessary for
        compliance with a legal obligation to which you are subject; (d)
        processing is related to a task that is carried out in the public
        interest or in the exercise of official authority vested in us; (e)
        processing is necessary for the purposes of the legitimate interests
        pursued by us or by a third party.
      </p>

      <p>
        Note that under some legislations we may be allowed to process
        information until you object to such processing by opting out, without
        having to rely on consent or any other of the legal bases. In any case,
        we will be happy to clarify the specific legal basis that applies to the
        processing, and in particular whether the provision of Personal
        Information is a statutory or contractual requirement, or a requirement
        necessary to enter into a contract.
      </p>

      <h2 className="h5 mt-4">Managing Information</h2>
      <p>
        You are able to delete certain Personal Information we have about you.
        The Personal Information you can delete may change as the Mobile
        Application and Services change. When you delete Personal Information,
        however, we may maintain a copy of the unrevised Personal Information in
        our records for the duration necessary to comply with our obligations to
        our affiliates and partners, and for the purposes described below. If
        you would like to delete your Personal Information or permanently delete
        your account, you can do so on the settings page of your account in the
        Mobile Application.
      </p>

      <h2 className="h5 mt-4">Disclosure of Information</h2>
      <p>
        To maintain the highest level of privacy and to protect your Personal
        Information to the full extent, we do not share your Personal
        Information with any third parties.
      </p>

      <p>
        However, we may also disclose any Personal Information we collect, use
        or receive if required or permitted by law, such as to comply with a
        subpoena or similar legal process, and when we believe in good faith
        that disclosure is necessary to protect our rights, protect your safety
        or the safety of others, investigate fraud, or respond to a government
        request.
      </p>

      <h2 className="h5 mt-4">Retention of Information</h2>
      <p>
        We will retain and use your Personal Information for the period
        necessary to comply with our legal obligations, as long as your user
        account remains active, until the purchase order or Services are
        fulfilled, to enforce our Policy, resolve disputes, and unless a longer
        retention period is required or permitted by law..
      </p>

      <p>
        We may use any aggregated data derived from or incorporating your
        Personal Information after you update or delete it, but not in a manner
        that would identify you personally. Once the retention period expires,
        Personal Information shall be deleted. Therefore, the right to access,
        the right to erasure, the right to rectification, and the right to data
        portability cannot be enforced after the expiration of the retention
        period.
      </p>

      <h2 className="h5 mt-4">Transfer of Information</h2>
      <p>
        We do not transfer or store your Personal Information outside your
        country of residence. All Personal Information collected is processed
        and maintained within your jurisdiction, in compliance with applicable
        data protection laws. In cases where local regulations require specific
        security measures for data storage and processing, we ensure full
        compliance by implementing appropriate technical and organizational
        safeguards.
      </p>

      <p>
        If any changes to our data storage practices occur, we will update this
        policy accordingly and notify you as required by law. Note that we are
        dedicated to ensuring the security of your personal data, adhering
        strictly to the guidelines outlined in our privacy notice and conforming
        to the applicable legal requirements.
      </p>

      <h2 className="h5 mt-4">Region Specific Notices</h2>
      <p>
        Out of respect for your privacy, we have implemented additional measures
        to comply with the obligations and rights associated with the collection
        of Personal Information as dictated by the laws governing the regions of
        our users.
      </p>

      <h2 className="h5 mt-4">Disclosures for residents of the USA</h2>
      <p>
        If you are a resident of California, Colorado, Connecticut, Delaware,
        Iowa, Maryland, Utah, or Virginia, you have certain rights and we aim to
        take reasonable steps to allow you to correct, amend, delete, or limit
        the use of your Personal Information. This supplemental section,
        together with other relevant sections of the Policy, provides
        information about your rights and how to exercise them under the
        California Consumer Privacy Act and the California Privacy Rights Act
        (collectively, “CCPA”), the Colorado Privacy Act (“CPA”), the
        Connecticut Data Privacy Act (“CDPA”), the Delaware Online Privacy and
        Protection Act (“DOPPA”), the Iowa Consumer Data Protection Act
        (“ICDPA”), the Maryland Personal Information Protection Act (“PIPA”),
        the Utah Consumer Privacy Act (“UCPA”), the Virginia Consumer Data
        Protection Act (“VCDPA”), and any and all regulations arising therefrom.
        Unless otherwise expressly stated, all terms in this section have the
        same meaning as defined in the related state laws.
      </p>

      <p>
        In addition to the rights as explained in this Policy, if you provide
        Personal Information as defined in the statute to obtain Services for
        personal, family, or household use, you have the right to submit
        requests related to your Personal Information once a calendar year. Note
        that there are circumstances when we may not be able to comply with your
        request such as when we are not able to verify your request or find that
        providing a full response conflicts with other legal obligations or
        regulatory requirements. You will be notified if it’s the case..
      </p>

      <p>
        (a) Right to know and right to access: You have the right to request
        certain information we have collected about you. Once we receive and
        confirm a verifiable request from you, we will disclose to you, to the
        extent permitted by law:
      </p>

      <ul>
        <li>The specific pieces of Personal Information we hold about you.</li>
        <li>
          The categories of sources from which Information about you is
          collected.
        </li>
        <li>
          The purposes for collecting, selling, or sharing your Personal
          Information.
        </li>
      </ul>

      <p>
        You have the right to request that the Personal Information is delivered
        in a format that is both portable and easily usable, as long as it is
        technically possible to do so.
      </p>

      <p>
        (b) Right to correct: You have the right to request that we correct your
        inaccurate Personal Information taking into account the nature of the
        Personal Information and the purposes of the processing of the Personal
        Information.
      </p>

      <p>
        (c) Right to delete: You have the right to request deletion of your
        Personal Information.
      </p>

      <p>
        (d) Right to opt-out of the sale and sharing: You have the right to
        opt-out of the sale of your Personal Information which may include
        selling, disclosing, or transferring Personal Information to another
        business or a third party for monetary or other valuable consideration.
      </p>

      <p>
        (e) Right to consent to or limit the use of your sensitive personal
        information: You have the right to consent to the use of your Sensitive
        Personal information and to direct us to restrict its use and disclosure
        solely to what is essential for carrying out or delivering the Services
        in a manner reasonably anticipated by an average user, or for certain
        business objectives as specified by law. However, we do not use
        Sensitive Personal Information for any purposes other than those legally
        permitted or beyond the scope of your consent.
      </p>

      <p>
        (f) Right to non-discrimination: You have the right to not be
        discriminated against in the Services or quality of Services you receive
        from us for exercising your rights. We may not, and will not, treat you
        differently because of your data subject request activity, and we may
        not and will not deny goods or Services to you, charge different rates
        for goods or Services, provide a different level quality of goods or
        Services, or suggest that we would treat you differently because of your
        data subject request activity.
      </p>

      <p>
        (g) Shine the Light: California residents that have an established
        business relationship with us have the right to know how their personal
        information is disclosed to third parties for their direct marketing
        purposes under California’s “Shine the Light” law, or the right to opt
        out of such practices.
      </p>

      <p>
        To exercise any of your rights, simply contact us using the details
        below. After we receive and verify your request, we will process it to
        the extent possible within our capabilities.
      </p>

      <h2 className="h5 mt-4">Other countries and general privacy rights</h2>
      <p>
        If you reside in a country not specifically mentioned in this policy, we
        are committed to protecting your personal data in accordance with
        internationally recognized privacy principles. Users from these regions
        may have rights similar to those outlined above and other data
        protection laws. These rights include:
      </p>

      <ul>
        <li>The right to access, correct, or delete your personal data</li>
        <li>The right to restrict or object to certain types of processing.</li>
        <li>The right to withdraw consent for data processing.</li>
        <li>The right to data portability, where applicable.</li>
        <li>
          The right to opt out of targeted advertising and data sales, where
          required by law.
        </li>
        <li>
          The right to file a grievance with a relevant data protection
          authority.
        </li>
        <li>
          The right to nominate a representative to exercise your privacy rights
          on your behalf, if permitted by applicable regulations.
        </li>
      </ul>

      <h2 className="h5 mt-4">How to Exercise Your Rights</h2>
      <p>
        Any requests to exercise your rights can be directed to us through the
        contact details provided in this document. Please note that we may ask
        you to verify your identity before responding to such requests. Your
        request must provide sufficient information that allows us to verify
        that you are the person you are claiming to be or that you are the
        authorized representative of such person. If we receive your request
        from an authorized representative, we may request evidence that you have
        provided such an authorized representative with power of attorney or
        that the authorized representative otherwise has valid written authority
        to submit requests on your behalf
      </p>

      <p>
        You must include sufficient details to allow us to properly understand
        the request and respond to it. We cannot respond to your request or
        provide you with Personal Information unless we first verify your
        identity or authority to make such a request and confirm that the
        Personal Information relates to you.
      </p>

      <h2 className="h5 mt-4">Privacy of Children</h2>
      <p>
        We do not knowingly collect any Personal Information from children under
        the age of 13. If you are under the age of 13, please do not submit any
        Personal Information through the Mobile Application and Services. If you
        have reason to believe that a child under the age of 13 has provided
        Personal Information to us through the Mobile Application and Services,
        please contact us to request that we delete that child’s Personal
        Information from our Services.
      </p>

      <p>
        We encourage parents and legal guardians to monitor their children’s
        Internet usage and to help enforce this Policy by instructing their
        children never to provide Personal Information through the Mobile
        Application and Services without their permission. We also ask that all
        parents and legal guardians overseeing the care of children take the
        necessary precautions to ensure that their children are instructed to
        never give out Personal Information when online without their permission
      </p>

      <h2 className="h5 mt-4">Links to Other Resources</h2>
      <p>
        The Mobile Application and Services contain links to other resources
        that are not owned or controlled by us. Please be aware that we are not
        responsible for the privacy practices of such other resources or third
        parties. We encourage you to be aware when you leave the Mobile
        Application and Services and to read the privacy statements of each and
        every resource that may collect Personal Information.
      </p>

      <p>
        The Mobile Application and Services contain links to other resources
        that are not owned or controlled by us. Please be aware that we are not
        responsible for the privacy practices of such other resources or third
        parties. We encourage you to be aware when you leave the Mobile
        Application and Services and to read the privacy statements of each and
        every resource that may collect Personal Information.
      </p>

      <h2 className="h5 mt-4">Information Security</h2>
      <p>
        We secure information you provide on computer servers in a controlled,
        secure environment, protected from unauthorized access, use, or
        disclosure. We maintain reasonable administrative, technical, and
        physical safeguards in an effort to protect against unauthorized access,
        use, modification, and disclosure of Personal Information in our control
        and custody. However, no data transmission over the Internet or wireless
        network can be guaranteed.
      </p>

      <p>
        Therefore, while we strive to protect your Personal Information, you
        acknowledge that (a) there are security and privacy limitations of the
        Internet which are beyond our control; (b) the security, integrity, and
        privacy of any and all information and data exchanged between you and
        the Mobile Application and Services cannot be guaranteed; and (c) any
        such information and data may be viewed or tampered with in transit by a
        third party, despite best efforts.
      </p>

      <p>
        As the security of Personal Information depends in part on the security
        of the device you use to communicate with us and the security you use to
        protect your credentials, please take appropriate measures to protect
        this information.
      </p>

      <h2 className="h5 mt-4">Data Breach</h2>
      <p>
        In the event we become aware that the security of the Mobile Application
        and Services has been compromised or Users’ Personal Information has
        been disclosed to unrelated third parties as a result of external
        activity, including, but not limited to, security attacks or fraud, we
        reserve the right to take reasonably appropriate measures, including,
        but not limited to, investigation and reporting, as well as notification
        to and cooperation with law enforcement authorities.
      </p>

      <p>
        In the event of a data breach, we will make reasonable efforts to notify
        affected individuals if we believe that there is a reasonable risk of
        harm to the User as a result of the breach or if notice is otherwise
        required by law. When we do, we will send you an email. In jurisdictions
        where required, we may also report the breach to relevant authorities in
        accordance with applicable data protection regulations.
      </p>

      <h2 className="h5 mt-4">Changes and Amendments</h2>
      <p>
        We reserve the right to modify this Policy or its terms related to the
        Mobile Application and Services at any time at our discretion. When we
        do, we will post a notification in the Mobile Application. We may also
        provide notice to you in other ways at our discretion, such as through
        the contact information you have provided.
      </p>

      <p>
        An updated version of this Policy will be effective immediately upon the
        posting of the revised Policy unless otherwise specified. Your continued
        use of the Mobile Application and Services after the effective date of
        the revised Policy (or such other act specified at that time) will
        constitute your consent to those changes. However, we will not, without
        your consent, use your Personal Information in a manner materially
        different than what was stated at the time your Personal Information was
        collected.
      </p>

      <h2 className="h5 mt-4">Acceptance of This Policy</h2>
      <p>
        You acknowledge that you have read this Policy and agree to all its
        terms and conditions. By accessing and using the Mobile Application and
        Services and submitting your information you agree to be bound by this
        Policy. If you do not agree to abide by the terms of this Policy, you
        are not authorized to access or use the Mobile Application and Service
      </p>

      <h2 className="h5 mt-4">Contacting Us</h2>

      <p>
        If you have any questions regarding the information we may hold about
        you or if you wish to exercise your rights, you may use the following
        data subject request form to submit your request:
      </p>

      <p>
        <strong></strong>{" "}
        <a href="https://app.websitepolicies.com/dsar/view/t2uu2kby">
          Submit a data access request:
        </a>
      </p>

      <p>
        If you have any other questions, concerns, or complaints regarding this
        Policy, we encourage you to contact us using the details below:
      </p>

      <p>
        <strong></strong>{" "}
        <a href="mailto:sirilexelot@gmail.com">sirilexelot@gmail.com</a>
      </p>

      <p>
        We will attempt to resolve complaints and disputes and make every
        reasonable effort to honor your wish to exercise your rights as quickly
        as possible and in any event, within the timescales provided by
        applicable data protection laws.
      </p>

      <p>
        If you believe your concerns have not been adequately addressed, you may
        escalate the matter to the appropriate data protection authority in your
        region, in accordance with applicable privacy laws.
      </p>

      <p className="text-muted small mt-4">
        This document was last updated on April 20, 2025
      </p>
    </div>
  );
};

export default PrivacyPolicy;
